<template>
  <div
    v-show="isFootVisible"
     :style="{
      width: `${footStyle.width + 2 * Foot.BORDER_WIDTH}px`,
      height: `${footStyle.height + 2 * Foot.BORDER_WIDTH}px`,
      marginTop: `-${footStyle.height / 2 + 2 * Foot.BORDER_WIDTH}px`,
    }"
    class="ui-game__foot">
      <canvas ref="canvas" :width="footStyle.width" :height="footStyle.height"></canvas>
  </div>
</template>
<script setup>
import { computed, onMounted, ref } from 'vue';
import { store } from '@/components/ui-game/store';
import { Foot, MoveType } from '@/enums';
import { addLeadingZero, getIsValueEqualsTrue, hexToColor } from '@/utils';

const data = [
  "0:0:0000000000000000",
  "1:0:0000000000000000",
  "2:0:0000000000000000",
  "3:0:0000000000000000",
  "4:0:0000000000000000",
  "5:0:0000000000000000",
  "6:0:0000000000000000",
  "7:0:0000000000000000",
  "8:0:0000000000000000",
  "9:0:0000000000000000",
  "a:0:0000000000000000",
  "b:0:0000000000000000",
  "c:0:0000000000000000",
  "d:0:0000000000000000",
  "e:0:0000000000000000",
  "f:0:0000000000000000",
  "10:0:0000000000000000",
  "11:0:0000000000000000",
  "12:0:0000000000000000",
  "13:0:0000000000000000",
  "14:0:0000000000000000",
  "15:0:0000000000000000",
  "16:0:0000000000000000",
  "17:0:0000000000000000",
  "18:0:0000000000000000",
  "19:0:0000000000000000",
  "1a:0:0000000000000000",
  "1b:0:0000000000000000",
  "1c:0:0000000000000000",
  "1d:0:0000000000000000",
  "1e:0:0000000000000000",
  "1f:0:0000000000000000",
];

const props = defineProps({
  isRegisterMode: Boolean,
});

const canvas = ref(null);
const previousFootKey = ref();
const previousFootState = ref();

const isFootVisible = computed(() =>
  getIsValueEqualsTrue(store.state.footOnlyMode) ||
  (getIsValueEqualsTrue(store.state.showFootPrint) && (store.state.isPlaying || store.state.isPaused)));

const footStyle = computed(() => ({
  width: getIsValueEqualsTrue(store.state.footOnlyMode) ? Foot.FOOT_ONLY_WIDTH : Foot.FOOT_WIDTH,
  height: getIsValueEqualsTrue(store.state.footOnlyMode) ? Foot.FOOT_ONLY_HEIGHT : Foot.FOOT_HEIGHT,
}));

const parse = (value) => {
  const [row] = value.split(':');
  const rowInfo = addLeadingZero(row).toLowerCase();
  const currentRowIndex = data.findIndex((record) => addLeadingZero(record.split(':')[0]).toLowerCase() === rowInfo);

  if (currentRowIndex !== -1) {
    data.splice(currentRowIndex, 1, value);
  }

  render();
};

const parseHexString = (hex) => {
  const result = [];
  let i = 0;

  while (i < hex.length) {
    const current = hex[i];
    const next = hex[i + 1];

    if (current && !next) {
      result.push(parseInt(`${current}`, 16));
      i += 1;
    } else if (current === '0' && next === '0') {
      result.push(0);
      i += 1;
    } else if (current === '0' && next !== '0') {
      if (hex[i + 2] && hex[i + 2] !== '0') {
        result.push(0);
        i += 1;
      } else {
        result.push(parseInt(`${current}${next}`, 16));
        i += 2;
      }
    } else if(current !== '0') {
      result.push(parseInt(`${current}${next}`, 16));
      i += 2;
    }
  }

  return result;
}

const getValues = (str) => {
  if (str.length === 2 * Foot.COLUMNS) {
    return str.match(/.{1,2}/g);
  }

  if (str.length === Foot.COLUMNS) {
    return str.match(/.{1}/g);
  }

  return parseHexString(str);
};

const render = () => {
  if (!canvas.value) {
    return;
  }

  const ctx = canvas.value.getContext('2d');
  const CELL_SIZE = footStyle.value.width / Foot.COLUMNS;

  // Note: flip vertically and horizontally
  ctx.scale(-1, -1);
  ctx.translate(-ctx.canvas.width, -ctx.canvas.height);

  data.forEach((record) => {
    const [rowInfo, , sensorValues] = record.split(':');
    const row = parseInt(rowInfo, 16);
    const str = sensorValues.replace(/\s/g, '');
    const values = getValues(str);

    for (let column = 0; column < Foot.COLUMNS; column++) {
      const sensorValue = values[column];

      ctx.fillStyle = hexToColor(sensorValue, store.state.footColorIntencity);
      ctx.fillRect(column * CELL_SIZE, row * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    }
  });

  // Note: reset
  ctx.setTransform(1, 0, 0, 1, 0, 0);
};

onMounted(() => {
  render();

  window.btReceived = (value) => {
    if (props.isRegisterMode) {
      return;
    }

    if (!Array.isArray(value)) {
      parse(value);
    } else {
      value.forEach(parse);
    }

    const [, key] = (Array.isArray(value) ? value[0] : value).split(':');

    if (store.data.move_type === MoveType.SINGLE && previousFootKey.value === key) {
      return;
    }

    previousFootKey.value = key;
    previousFootState.value = data;
    window.keyPressed(key, true);
  };
});

defineExpose({ previousFootState });
</script>
